import api from './api';

/**
 * Invite Service
 * 
 * API calls for invite system
 * Phase 4 M2: User Invite System
 */

const inviteService = {
  /**
   * Create invite (company owner only)
   * @param {Object} data - { email }
   * @returns {Promise<Object>}
   */
  createInvite: async (data) => {
    const response = await api.post('/company/invites', data);
    return response.data;
  },

  /**
   * Get company invites (with pagination, scope-aware)
   * @param {Object} params - { search, status, companyId (for system admin), page, limit }
   * @returns {Promise<Object>}
   */
  getInvites: async (params = {}) => {
    const { search, status, companyId, page, limit } = params;
    const queryParams = new URLSearchParams();
    
    if (search) queryParams.append('search', search);
    if (status) queryParams.append('status', status);
    if (companyId) queryParams.append('companyId', companyId);
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    
    const response = await api.get(`/company/invites?${queryParams}`);
    return response.data;
  },

  /**
   * Get invite statistics (company owner only)
   * @returns {Promise<Object>}
   */
  getInviteStats: async () => {
    const response = await api.get('/company/invites/stats');
    return response.data;
  },

  /**
   * Get invite by ID (company owner only)
   * @param {string} inviteId
   * @returns {Promise<Object>}
   */
  getInviteById: async (inviteId) => {
    const response = await api.get(`/company/invites/${inviteId}`);
    return response.data;
  },

  /**
   * Cancel invite (company owner only)
   * @param {string} inviteId
   * @returns {Promise<Object>}
   */
  cancelInvite: async (inviteId) => {
    const response = await api.delete(`/company/invites/${inviteId}`);
    return response.data;
  },

  /**
   * Resend invite (company owner only)
   * @param {string} inviteId
   * @returns {Promise<Object>}
   */
  resendInvite: async (inviteId) => {
    const response = await api.post(`/company/invites/${inviteId}/resend`);
    return response.data;
  },

  // ==================== PUBLIC/USER ROUTES ====================

  /**
   * Validate invite token (public)
   * @param {string} token
   * @returns {Promise<Object>}
   */
  validateInviteToken: async (token) => {
    const response = await api.get(`/invites/accept/${token}`);
    return response.data;
  },

  /**
   * Accept invite (existing user - authenticated)
   * @param {string} token
   * @returns {Promise<Object>}
   */
  acceptInvite: async (token) => {
    const response = await api.post(`/invites/accept/${token}`);
    return response.data;
  },

  /**
   * Reject invite (existing user - authenticated)
   * @param {string} token
   * @returns {Promise<Object>}
   */
  rejectInvite: async (token) => {
    const response = await api.post(`/invites/reject/${token}`);
    return response.data;
  },

  /**
   * Get user's pending invites (authenticated)
   * @returns {Promise<Object>}
   */
  getUserPendingInvites: async () => {
    const response = await api.get('/invites/pending');
    return response.data;
  },

  /**
   * Register with invite (new user - public)
   * @param {string} token
   * @param {Object} userData - { firstName, lastName, password }
   * @returns {Promise<Object>}
   */
  registerWithInvite: async (token, userData) => {
    const response = await api.post(`/invites/register/${token}`, userData);
    return response.data;
  }
};

export default inviteService;
